import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { TrendingUp, Eye, EyeOff, Activity, Briefcase, Scale, PieChart } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'

const HIGHLIGHTS = [
  { icon: Activity,  label: 'Live market streaming' },
  { icon: Briefcase, label: 'Unified 4-account portfolio' },
  { icon: Scale,     label: 'Risk-aware rebalancing' },
  { icon: PieChart,  label: 'Mutual-fund intelligence' },
]

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    const result = await login(formData.email, formData.password)
    if (!result.success) setLoading(false)
  }

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))

  return (
    <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-5xl mx-auto">
        {/* Left — value prop */}
        <div className="hidden lg:block">
          <div className="kicker text-accent-600 mb-3">Trading intelligence</div>
          <h1 className="font-display font-semibold text-ink-900 text-[40px] leading-[1.05] tracking-tight">
            One calm command center for your entire portfolio.
          </h1>
          <p className="mt-5 text-lg text-ink-500 leading-7 max-w-md">
            Live data, unified accounts, fund research and a private workspace — held to an
            institutional standard, built for one disciplined investor.
          </p>
          <ul className="mt-8 space-y-3">
            {HIGHLIGHTS.map((h) => (
              <li key={h.label} className="flex items-center gap-3 text-ink-700">
                <span className="w-9 h-9 rounded-xl bg-white border border-ink-100 shadow-elev-1 flex items-center justify-center">
                  <h.icon className="w-4 h-4 text-accent-600" />
                </span>
                <span className="text-[15px] font-medium">{h.label}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-sm text-ink-400">
            New here? <Link to="/about" className="text-accent-700 font-medium hover:text-accent-800">Learn about serenDPT →</Link>
          </p>
        </div>

        {/* Right — sign-in card */}
        <div className="w-full max-w-md mx-auto">
          <div className="card shadow-elev-3">
            <div className="text-center mb-6">
              <div className="flex justify-center">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-accent-500 to-accent-700 shadow-glow-accent flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
              </div>
              <h2 className="mt-4 font-display font-semibold text-2xl text-ink-900 tracking-tight">Welcome back</h2>
              <p className="mt-1.5 text-sm text-ink-500">Sign in to your serenDPT workspace</p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-ink-700 mb-1">Email address</label>
                <input
                  id="email" name="email" type="email" autoComplete="email" required
                  value={formData.email} onChange={handleChange}
                  className="input" placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-ink-700 mb-1">Password</label>
                <div className="relative">
                  <input
                    id="password" name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password" required
                    value={formData.password} onChange={handleChange}
                    className="input pr-10" placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-ink-400 hover:text-ink-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className="w-full btn btn-primary flex items-center justify-center">
                {loading ? <LoadingSpinner size="small" /> : 'Sign in'}
              </button>

              <p className="text-center text-sm text-ink-500">
                Don’t have an account?{' '}
                <Link to="/register" className="font-medium text-accent-700 hover:text-accent-800">Create one</Link>
              </p>
            </form>
          </div>

          <p className="mt-5 text-center text-xs text-ink-400">
            By continuing you agree to our{' '}
            <Link to="/terms" className="text-ink-500 hover:text-accent-700 underline">Terms</Link> and{' '}
            <Link to="/privacy" className="text-ink-500 hover:text-accent-700 underline">Privacy</Link>.
          </p>
        </div>
      </div>
    </div>
  )
}

export default Login
